import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../shared/hooks/useAuth';
import API_BASE_URL from '../../../config/api';
import api from '../../../shared/utils/api';
import { convertToMongolian, convertRegistrationNumber, hasConvertibleEnglishLetters } from '../../../shared/utils/mongolianConverter';
import { getRegistrationNumberType, shouldTriggerApiCheck } from '../../customers/utils/ebarimtHelper';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import SuccessPopup from '../../../shared/components/SuccessPopup';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import WarningPopup from '../../../shared/components/WarningPopup';

// Debounce utility function
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

interface AppUser {
  [key: string]: any;
}

/** API /customers-for-dropdown returns `CustomerOid` (not `Oid`). */
const getCustomerRowId = (c: { CustomerOid?: string; Oid?: string }) =>
  String(c.CustomerOid ?? c.Oid ?? '');

const AccountantUserPage: React.FC = () => {
  const { userAuth } = useAuth();
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [originalAppUsers, setOriginalAppUsers] = useState<AppUser[]>([]); // Store original data with GUIDs
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editRowData, setEditRowData] = useState<AppUser | null>(null);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [form, setForm] = useState({
    UserName: '',
    FirstName: '',
    LastName: '',
    Password: '',
    RoleOid: '',
    BankAccountnum: '',
    CustomerID: '',
    Customers: false,
    Employee: true, // Always checked by default
    IsCitizen: false
  });

  const [roles, setRoles] = useState<Array<{Oid: string, RoleName: string}>>([]);
  const [customers, setCustomers] = useState<Array<{ CustomerOid?: string; Oid?: string; CustomerID: string; CustomerName: string }>>([]);
  
  // Additional state for AdminAppUserPage functionality
  const [registerNoError, setRegisterNoError] = useState('');
  const [isLoadingEBarimt, setIsLoadingEBarimt] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [customersFetched, setCustomersFetched] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState<Array<{ CustomerOid?: string; Oid?: string; CustomerID: string; CustomerName: string }>>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSelectionError, setCustomerSelectionError] = useState('');
  const customerComboRef = useRef<HTMLDivElement>(null);

  // Success popup state
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  // SmartTableButtons state
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Debounced conversion function to allow both characters to be typed
  const debouncedConvertToMongolian = useCallback(
    debounce((value: string, setter: (value: string) => void) => {
      console.log('🔥 Debounced conversion called with:', value);
      
      // Check for English letters BEFORE any conversion
      if (hasConvertibleEnglishLetters(value)) {
        console.log('🔥 Debounced conversion - has convertible letters, converting...');
        const convertedValue = convertRegistrationNumber(value);
        console.log('🔥 Debounced conversion - after conversion:', convertedValue);
        
        // Convert to uppercase after Mongolian conversion
        const finalValue = convertedValue.toUpperCase();
        console.log('🔥 Debounced conversion - after uppercase:', finalValue);
        
        setter(finalValue);
      } else {
        // If no English letters, just convert to uppercase
        const finalValue = value.toUpperCase();
        setter(finalValue);
      }
    }, 300),
    []
  );

  const transformAppUserData = (users: AppUser[]) => {
    return users.map(user => {
      const transformed: any = { ...user };
      
      // Handle GUID fields properly - they are uniqueidentifier, not boolean
      if (user.Accountant !== undefined && user.Accountant !== null) {
        transformed.Accountant = user.Accountant ? 'Тийм' : 'Үгүй';
      } else {
        transformed.Accountant = 'Үгүй';
      }
      


      // Handle accountant data (from Accountants table)
      if (user.RoleType === 'Accountant') {
        // For accountant records, use their own info
        transformed.CustomerName = 'Нягтлан бодогч';
        transformed.Employee = 'Үгүй';
        transformed.Customers = 'Үгүй';
        transformed.IsCitizen = 'Үгүй';
        transformed.BankAccountnum = user.Phone || 'Тодорхойгүй';
        transformed.isAccountant = true; // Flag to identify accountant rows
      }

      // Handle customer data (from Customers table)
      if (user.RoleType === 'Customer') {
        // For customer records, use their own info
        transformed.CustomerName = user.CustomerName || user.FirstName;
        transformed.Employee = 'Үгүй';
        transformed.Customers = 'Тийм';
        transformed.IsCitizen = 'Үгүй';
        transformed.BankAccountnum = user.TinCode || 'Тодорхойгүй';
        transformed.isCustomer = true; // Flag to identify customer rows
      }
      

      
      if (user.Customers !== undefined && user.Customers !== null) {
        transformed.Customers = (user.Customers === 1 || user.Customers === 'Тийм') ? 'Тийм' : 'Үгүй';
      } else {
        transformed.Customers = 'Үгүй';
      }
      
      if (user.Employee !== undefined && user.Employee !== null) {
        transformed.Employee = (user.Employee === 1 || user.Employee === 'Тийм') ? 'Тийм' : 'Үгүй';
      } else {
        transformed.Employee = 'Үгүй';
      }
      
      if (user.IsCitizen !== undefined && user.IsCitizen !== null) {
        transformed.IsCitizen = (user.IsCitizen === 1) ? 'Тийм' : 'Үгүй';
      } else {
        transformed.IsCitizen = 'Үгүй';
      }
      
      // Format CustomerName - show "Хоосон" if null/empty
      if (!user.CustomerName || user.CustomerName.trim() === '') {
        transformed.CustomerName = 'Хоосон';
      }
      
      // Format BankAccountnum - show "Хоосон" if null/empty
      if (!user.BankAccountnum || user.BankAccountnum.trim() === '') {
        transformed.BankAccountnum = 'Хоосон';
      }
      
      // Format CreatedDate
      if (user.CreatedDate) {
        try {
          const date = new Date(user.CreatedDate);
          transformed.CreatedDate = date.toLocaleDateString('mn-MN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (error) {
          transformed.CreatedDate = 'Огноо алдаа';
        }
      } else {
        transformed.CreatedDate = 'Хоосон';
      }
      
      return transformed;
    });
  };

  const fetchAppUsers = async () => {
    setLoading(true);
    try {
      // AccountantOid-аар хэрэглэгчдийн жагсаалт авах
      const accountantOid = localStorage.getItem('accountantOid');
      if (!accountantOid) {
        console.error('AccountantOid not found in userAuth');
        alert('Нягтлан бодогчийн мэдээлэл олдсонгүй');
        return;
      }
      
      const result = await api.get(`/api/users/userlist/accountant/${accountantOid}`);
      console.log('🔥 Frontend - Raw user data from API:', result.data);
      if (result.success) {
        // Log specific user data for debugging
        result.data.forEach((user: any, index: number) => {
          console.log(`🔥 User ${index} - Raw data:`, {
            Oid: user.Oid,
            UserName: user.UserName,
            Customers: user.Customers,
            CustomerID: user.CustomerID,
            CustomerName: user.CustomerName,
            Employee: user.Employee,
            IsCitizen: user.IsCitizen
           
          });
        });
        
        // Store original data with GUIDs
        setOriginalAppUsers(result.data);
        
        // Transform the data for display
        const transformedData = transformAppUserData(result.data);
        console.log('🔥 Frontend - Transformed user data:', transformedData);
        
        // Log specific transformed data for debugging
        transformedData.forEach((user: any, index: number) => {
          console.log(`🔥 User ${index} - Transformed data:`, {
            Oid: user.Oid,
            UserName: user.UserName,
            Customers: user.Customers,
            Employee: user.Employee,
            IsCitizen: user.IsCitizen,
            CustomerName: user.CustomerName
          });
        });
        setAppUsers(transformedData);
      } else {
        console.error('Error fetching users:', result.message);
        alert('Хэрэглэгчдийн мэдээлэл ачаалахад алдаа гарлаа');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Хэрэглэгчдийн мэдээлэл ачаалахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  

  const fetchCustomers = async () => {
    try {
      const result = await api.get('/api/customers-for-dropdown');
      if (result.success) {
        setCustomers(result.data);
        setCustomersFetched(true);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };



  const continueEditProcess = async (originalUser: AppUser, user: AppUser) => {
    setEditingUser(originalUser);
    
    // Check if this is an accountant or customer record
    if (originalUser.RoleType === 'Accountant') {
      // For accountant records, show a different form or message
      alert('Нягтлан бодогчийн мэдээллийг засах боломжгүй. Энэ бол таны өөрийн мэдээлэл юм.');
      return;
    }
    
    if (originalUser.RoleType === 'Customer') {
      // For customer records, show a different form or message
      alert('Харилцагчийн мэдээллийг засах боломжгүй. Харилцагчдийн хуудас руу очно уу.');
      return;
    }
    
    // First fetch customers to find CustomerOid
    await fetchCustomers();
    
    // Find customer Oid from CustomerID (registration number)
    let customerOid = originalUser.CustomerOid || '';
    
    // If CustomerOid is not available, try to find it from customers list using CustomerID
    if (!customerOid && originalUser.CustomerID && customers.length > 0) {
      const foundCustomer = customers.find(c => c.CustomerID === originalUser.CustomerID);
      if (foundCustomer) {
        customerOid = getCustomerRowId(foundCustomer);
        console.log('🔥 Found CustomerOid from customers list:', customerOid);
      }
    }
    
    console.log('🔥 continueEditProcess - CustomerOid:', customerOid, 'CustomerID:', originalUser.CustomerID);
    
    const formData = {
      UserName: originalUser.UserName || '',
      FirstName: originalUser.FirstName || '',
      LastName: originalUser.LastName || '',
      Password: '',
      CustomerID: customerOid, // Use CustomerOid (GUID)
      Customers: originalUser.Customers === 'Тийм' || originalUser.Customers === 1 || originalUser.Customers === true,
      Employee: originalUser.Employee === 'Тийм' || originalUser.Employee === 1 || originalUser.Employee === true,
      IsCitizen: originalUser.IsCitizen === 'Тийм' || originalUser.IsCitizen === 1,
      BankAccountnum: originalUser.BankAccountnum || '',
      RoleOid: originalUser.RoleOid || ''
    };
    
    setForm(formData);
    setEditing(true);
  };

  const handleDeleteUser = async (userOid: string) => {
    try {
      const result = await api.delete(`/api/accountant/users/${userOid}`);
      if (result.success) {
        setSuccessMessage('Хэрэглэгч амжилттай устгагдлаа');
        setShowSuccessPopup(true);
        fetchAppUsers();
      } else {
        alert('Хэрэглэгч устгахад алдаа гарлаа: ' + result.message);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Хэрэглэгч устгахад алдаа гарлаа');
    }
  };

  // Helper function to find next available field
  const findNextAvailableField = (currentTabIndex: number) => {
    const tabIndexes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const currentIndex = tabIndexes.indexOf(currentTabIndex);
    
    for (let i = currentIndex + 1; i < tabIndexes.length; i++) {
      const nextTabIndex = tabIndexes[i];
      const nextField = document.querySelector(`[tabindex="${nextTabIndex}"]`) as HTMLElement;
      if (nextField && !nextField.hasAttribute('disabled')) {
        console.log(`Found next available field with tabindex="${nextTabIndex}":`, nextField);
        return nextField;
      }
    }
    console.log('No next available field found');
    return null;
  };

  const isRequiredField = (fieldName: string) => {
    const requiredFields = ['UserName', 'FirstName', 'LastName'];
    return requiredFields.includes(fieldName);
  };

  const validateRequiredFields = () => {
    const requiredFields = ['UserName', 'FirstName', 'LastName'];
    return requiredFields.every(field => form[field as keyof typeof form] && form[field as keyof typeof form] !== '');
  };

  const renderLabel = (fieldName: string) => {
    const labels: { [key: string]: string } = {
      UserName: 'Хэрэглэгчийн регистрийн дугаар',
      FirstName: 'Нэр',
      LastName: 'Овог'
    };
    
    // Check if this field should be read-only in edit mode
    const isReadOnlyInEdit = editing && (fieldName === 'UserName' || fieldName === 'FirstName' || fieldName === 'LastName');
    
    return (
      <label className={`block text-sm font-medium mb-1 ${isReadOnlyInEdit ? 'text-gray-500' : 'text-gray-700'}`}>
        {labels[fieldName]} {isRequiredField(fieldName) && <span className="text-red-500 ml-1">*</span>}
        {isReadOnlyInEdit && <span className="text-gray-400 ml-2 text-xs">(Засварлах боломжгүй)</span>}
      </label>
    );
  };

  // Debounced function to check registration number
  const debouncedCheckRegisterNo = React.useCallback(
    React.useMemo(() => {
      let timeoutId: NodeJS.Timeout;
      return (value: string) => {
        clearTimeout(timeoutId);
        
        // Only trigger API check if the registration number has reached the correct length
        const shouldCheck = shouldTriggerApiCheck(value);
        
        if (shouldCheck) {
          timeoutId = setTimeout(() => {
            checkRegisterNo(value);
          }, 500);
        }
      };
    }, []),
    []
  );

  const checkRegisterNo = async (value: string) => {
    const regNo = value.trim();
    if (!regNo) return;

    // Validate registration number format
    if (!shouldTriggerApiCheck(regNo)) {
      setErrorMessage('Зөвхөн 7 ба түүнээс дээш тэмдэгт оруулна уу');
      setShowErrorPopup(true);
      setIsLoadingEBarimt(false);
      return;
    }

    // Additional validation for registration number format
    const hasLetters = /[A-Za-zА-Яа-я]/.test(regNo);
    const cleanRegNo = regNo.replace(/\D/g, '');
    
    if (hasLetters) {
      // For individual registration numbers with letters, must be at least 10 characters
      if (regNo.length < 10) {
        setErrorMessage('Хувь хүний регистрийн дугаар 10 тэмдэгт байх ёстой');
        setShowErrorPopup(true);
        setIsLoadingEBarimt(false);
        return;
      }
    } else {
      // For company registration numbers (pure numeric), must be exactly 7 digits
      if (cleanRegNo.length !== 7) {
        setErrorMessage('Байгууллагын регистрийн дугаар 7 оронтой байх ёстой');
        setShowErrorPopup(true);
        setIsLoadingEBarimt(false);
        return;
      }
    }

    setIsLoadingEBarimt(true);
    setRegisterNoError('');

    try {
      console.log('Fetching person info for regNo:', regNo);
      
      // First, check if user already exists for this accountant
      try {
        const existingUserCheck = await api.get(`/api/users/check-register-accountant/${regNo}`);
        if (!existingUserCheck.success) {
          setRegisterNoError(existingUserCheck.message);
          setIsLoadingEBarimt(false);
          return;
        }
      } catch (error) {
        console.error('Error checking existing user:', error);
        // Continue with eBarimt lookup even if check fails
      }
      
      // Use backend API instead of direct eBarimt calls
      console.log('Using backend API for eBarimt lookup');
      const backendRes = await api.get(`/api/check-ebarimt?regno=${regNo}`);
      
      if (!backendRes.success) {
        console.log('Backend API failed:', backendRes.message);
        // Show user-friendly message for specific eBarimt errors
        let warningMsg = backendRes.message || 'Регистрийн дугаараас мэдээлэл олдсонгүй';
        
        // Check if it's the specific TIN not found error
        if (backendRes.message && (
          backendRes.message.includes('бүртгэлгүй') ||
          backendRes.message.includes('TIN олдсонгүй')
        )) {
          warningMsg = backendRes.message;
        }
        
        setWarningMessage(warningMsg);
        setShowWarningPopup(true);
        setRegisterNoError(''); // Clear any existing error
        return;
      }
      
      const data = backendRes.data;
      const resolvedName = backendRes.name || data?.name;
      console.log('Backend API response:', data);
      
      if (resolvedName) {
        // Split the name into first and last name
        const nameParts = resolvedName.trim().split(' ');
        console.log('Name parts:', nameParts);
        
        if (nameParts.length >= 2) {
          // First part is last name, rest is first name
          const lastName = nameParts[0];
          const firstName = nameParts.slice(1).join(' ');
          console.log('Auto-filling name:', firstName, lastName);
          setForm(prev => ({
            ...prev,
            FirstName: firstName || '',
            LastName: lastName || '',
          }));
          setRegisterNoError(''); // Clear any error since we found valid data
        } else if (nameParts.length === 1) {
          // Only one name part, treat as first name
          console.log('Auto-filling single name:', nameParts[0]);
          setForm(prev => ({
            ...prev,
            FirstName: nameParts[0] || '',
            LastName: '',
          }));
          setRegisterNoError(''); // Clear any error since we found valid data
        } else {
          console.log('Invalid name format');
          setRegisterNoError('Нэр олдсонгүй эсвэл буруу форматтай байна');
        }
      } else {
        console.log('No name found in data');
        setRegisterNoError('Регистрийн дугаараас нэр олдсонгүй');
      }
    } catch (error) {
      console.error('Error checking registration number:', error);
      // Show user-friendly message for API errors
      let warningMsg = 'Регистрийн дугаар буруу тул дахин шалгана уу';
      
      // Check if it's the specific TIN not found error
      if (error.message && (
        error.message.includes('бүртгэлгүй') ||
        error.message.includes('TIN олдсонгүй')
      )) {
        warningMsg = error.message.includes('бүртгэлгүй')
          ? error.message
          : 'РД дугаар бүртгэлгүй байна';
      }
      
      setWarningMessage(warningMsg);
      setShowWarningPopup(true);
      setRegisterNoError(''); // Clear any existing error
    } finally {
      setIsLoadingEBarimt(false);
    }
  };

  useEffect(() => {
    fetchAppUsers();
   
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!showCustomerDropdown || editing) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const root = customerComboRef.current;
      if (!root || root.contains(e.target as Node)) return;
      setShowCustomerDropdown(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showCustomerDropdown, editing]);

  const columns = [
    { key: 'UserName', label: 'Хэрэглэгчийн нэр' },
    { key: 'FirstName', label: 'Нэр' },
    { key: 'LastName', label: 'Овог' },
    { key: 'CustomerName', label: 'Харилцагч' },
    { key: 'Employee', label: 'Ажилтан' },
    { key: 'Customers', label: 'Харилцагч' },
    { key: 'BankAccountnum', label: 'Банкны данс' },
    { key: 'IsCitizen', label: 'Иргэн' },
    { key: 'CreatedDate', label: 'Бүртгэсэн огноо' }
  ];

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Хэрэглэгчдийн удирдлага</h1>
          
          {/* SmartTableButtons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={() => setShowColumnSearch(!showColumnSearch)}
            onColumnSettings={() => {
              console.log('Column settings clicked');
              setShowColumnSettings(!showColumnSettings);
            }}
            onRefresh={() => {
              console.log('Refresh clicked');
              fetchAppUsers();
            }}
            onAdd={() => {
              setAdding(true);
              fetchCustomers(); // Refresh customers when adding new user
            }}
            addable={true}
          />
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">Уншиж байна...</div>
          </div>
        ) : (
          <SmartTable
            data={appUsers}
            columns={columns}
            settingsKey="accountant-users-table"
            fetchData={fetchAppUsers}
            showColumnSearch={showColumnSearch}
            showColumnSettings={showColumnSettings}
            onAdd={() => {
              setAdding(true);
              fetchCustomers(); // Refresh customers when adding new user
            }}
            onEdit={async (user) => {
              console.log('🔥 Frontend - Editing user data:', {
                Oid: user.Oid,
                UserName: user.UserName,
                Customers: user.Customers,
                Employee: user.Employee,
                IsCitizen: user.IsCitizen,
                RoleType: user.RoleType
              });
              
              // Find the original user data with GUIDs
              const originalUser = originalAppUsers.find(u => u.Oid === user.Oid);
              if (!originalUser) {
                console.error('Original user not found for Oid:', user.Oid);
                // Fallback logic to refetch users if originalAppUsers is empty
                                  if (originalAppUsers.length === 0) {
                    fetchAppUsers().then(() => {
                      setTimeout(async () => {
                        const retryOriginalUser = originalAppUsers.find(u => u.Oid === user.Oid);
                        if (retryOriginalUser) {
                          await continueEditProcess(retryOriginalUser, user);
                        }
                      }, 1000);
                    });
                    return;
                  }
                  return;
                }
                
                await continueEditProcess(originalUser, user);
            }}
            onDelete={(user) => {
              // Check if this is an accountant or customer record
              if (user.RoleType === 'Accountant') {
                alert('Нягтлан бодогчийн мэдээллийг устгах боломжгүй. Энэ бол таны өөрийн мэдээлэл юм.');
                return;
              }
              
              if (user.RoleType === 'Customer') {
                alert('Харилцагчийн мэдээллийг устгах боломжгүй. Харилцагчдийн хуудас руу очно уу.');
                return;
              }
              
              if (window.confirm('Хэрэглэгчийг устгахдаа итгэлтэй байна уу?')) {
                handleDeleteUser(user.Oid);
              }
            }}
            editable={true}
            addable={true}
          />
        )}

        {/* Add/Edit Modal */}
        {(adding || editing) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
                              <h2 className="text-xl font-bold mb-4 text-center text-blue-700">
                  {adding ? 'Хэрэглэгч нэмэх' : 'Хэрэглэгч засах'}
                </h2>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className={`block text-sm font-medium ${editing ? 'text-gray-500' : 'text-gray-700'}`}>
                      Байгууллага сонгох <span className="text-red-500 ml-1">*</span>
                      {editing && <span className="text-gray-400 ml-2 text-xs">(Зөвхөн харах)</span>}
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (!editing) {
                          setCustomersFetched(false);
                          setCustomersError('');
                          fetchCustomers();
                        }
                      }}
                      className={`text-xs ${editing ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                      disabled={editing}
                    >
                      🔄 Дахин ачаалах
                    </button>
                  </div>
                  <div className="relative" ref={customerComboRef}>
                    <input
                      type="text"
                      value={(() => {
                        if (editing && editingUser) {
                          // When editing, show CustomerName and CustomerID from editingUser
                          return `${editingUser.CustomerName || 'Тодорхойгүй'} (${editingUser.CustomerID || 'Тодорхойгүй'})`;
                        }
                        if (form.CustomerID) {
                          const selectedCustomer = customers.find(c => getCustomerRowId(c) === form.CustomerID);
                          return selectedCustomer ? `${selectedCustomer.CustomerName} (${selectedCustomer.CustomerID})` : form.CustomerID;
                        }
                        return form.CustomerID;
                      })()}
                      tabIndex={1}
                      placeholder="Байгууллагын нэр эсвэл регистрийн дугаараар хайх..."
                      onChange={(e) => {
                        if (!editing) { // Only allow changes when not editing
                          const value = e.target.value;
                          setForm(prev => ({ ...prev, CustomerID: value }));
                          setCustomerSelectionError(''); // Clear error when user starts typing
                          
                          // Filter customers based on search input
                          if (value.trim() === '') {
                            setFilteredCustomers(customers);
                          } else {
                            const filtered = customers.filter(customer => 
                              customer.CustomerName.toLowerCase().includes(value.toLowerCase()) ||
                              customer.CustomerID.includes(value)
                            );
                            setFilteredCustomers(filtered);
                          }
                        }
                      }}
                      onFocus={() => {
                        if (!editing) { // Only show dropdown when not editing
                          // Show dropdown when focused
                          setShowCustomerDropdown(true);
                          if (String(form.CustomerID ?? '').trim() === '') {
                            setFilteredCustomers(customers);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          const nextField = document.querySelector('input[tabindex="2"]') as HTMLElement;
                          if (nextField) {
                            nextField.focus();
                          }
                        }
                      }}
                      className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 transition ${
                        customerSelectionError 
                          ? 'border-red-500 focus:ring-red-400' 
                          : editing 
                            ? 'bg-gray-100 border-gray-300 cursor-default text-gray-700' 
                            : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      readOnly={editing}
                      disabled={editing}
                    />
                    
                    {/* Dropdown Button */}
                    <button
                      type="button"
                      onClick={() => {
                        if (!editing) {
                          setShowCustomerDropdown(!showCustomerDropdown);
                          if (!showCustomerDropdown && String(form.CustomerID ?? '').trim() === '') {
                            setFilteredCustomers(customers);
                          }
                        }
                      }}
                      className={`absolute inset-y-0 right-0 flex items-center px-3 focus:outline-none ${
                        editing ? 'text-gray-400 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'
                      }`}
                      disabled={editing}
                    >
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${showCustomerDropdown ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    
                    {/* Dropdown */}
                    {showCustomerDropdown && (
                      <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {customersLoading && !customersFetched ? (
                          <div className="px-3 py-2 text-sm text-gray-500">⏳ Уншиж байна...</div>
                        ) : customersError ? (
                          <div className="px-3 py-2 text-sm text-red-500">❌ Алдаа: {customersError}</div>
                        ) : filteredCustomers.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">📭 Байгууллага олдсонгүй</div>
                        ) : (
                          filteredCustomers.map((customer) => (
                            <div
                              key={getCustomerRowId(customer)}
                              role="option"
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setForm(prev => ({
                                  ...prev,
                                  CustomerID: getCustomerRowId(customer)
                                }));
                                setCustomerSelectionError('');
                                setShowCustomerDropdown(false);
                              }}
                            >
                              <div className="font-medium">{customer.CustomerName}</div>
                              <div className="text-gray-500 text-xs">{customer.CustomerID}</div>
                            </div>
                          ))
                        )}
                      </div>
                                            )}
                      </div>
                      {customerSelectionError && (
                        <p className="text-red-500 text-xs mt-1">{customerSelectionError}</p>
                      )}
                    </div>


                
                <div>
                  {renderLabel('UserName')}
                  <div className="relative">
                    <input
                    type="text"
                    value={form.UserName}
                    tabIndex={2}
                    onChange={(e) => {
                      if (!editing) { // Only allow changes when not editing
                        const value = e.target.value;
                        
                        // Set the raw value immediately for responsive UI
                        setForm(prev => ({ ...prev, UserName: value }));
                        
                        // Clear error when user starts typing
                        if (value === '') {
                          setRegisterNoError('');
                          return;
                        }
                        
                        // Use debounced conversion to allow both characters to be typed
                        debouncedConvertToMongolian(value, (convertedValue) => {
                          setForm(prev => ({ ...prev, UserName: convertedValue }));
                          
                          // Real-time format validation
                          if (convertedValue && (convertedValue.length === 8 || convertedValue.length === 10)) {
                            if (!/^([A-ZА-Я]{2}\d{8}|\d{8})$/.test(convertedValue)) {
                              setRegisterNoError('Регистрийн дугаар 2 үсэг + 8 оронтой тоо эсвэл 8 оронтой тоо байх ёстой');
                              return;
                            }
                          }
                          
                          // Trigger debounced API check with converted value
                          debouncedCheckRegisterNo(convertedValue);
                        });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        
                        // If EBarimt is loading, skip to FirstName field (tabindex 3) to avoid auto-filled fields
                        if (isLoadingEBarimt) {
                          const firstNameField = document.querySelector('input[tabindex="3"]') as HTMLElement;
                          if (firstNameField) {
                            firstNameField.focus();
                          }
                          return;
                        }
                        
                        // Find next available field
                        const nextField = document.querySelector('input[tabindex="3"], select[tabindex="3"]') as HTMLElement;
                        if (nextField && !nextField.hasAttribute('disabled')) {
                          nextField.focus();
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      registerNoError 
                        ? 'border-red-500' 
                        : editing 
                          ? 'bg-gray-200 border-gray-400 cursor-not-allowed text-gray-600' 
                          : 'border-gray-300'
                    }`}
                    disabled={editing}
                    readOnly={editing}
                  />
                    {isLoadingEBarimt && (
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      </div>
                    )}
                  </div>
                  {registerNoError && (
                    <p className="text-red-500 text-sm mt-1">{registerNoError}</p>
                  )}
                </div>

                <div>
                  {renderLabel('FirstName')}
                  <input
                    type="text"
                    value={form.FirstName}
                    tabIndex={3}
                    onChange={(e) => {
                      if (!editing) { // Only allow changes when not editing
                        setForm(prev => ({ ...prev, FirstName: e.target.value }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        // Find next available field
                        const nextField = document.querySelector('input[tabindex="4"], select[tabindex="4"]') as HTMLElement;
                        if (nextField && !nextField.hasAttribute('disabled')) {
                          nextField.focus();
                        }
                      }
                    }}
                    disabled={(form.FirstName !== '' && !editing) || isLoadingEBarimt || editing}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      (form.FirstName !== '' && !editing) || isLoadingEBarimt || editing ? 'bg-gray-200 border-gray-400 cursor-not-allowed text-gray-600' : 'border-gray-300'
                    }`}
                    readOnly={editing}
                  />
                </div>

                <div>
                  {renderLabel('LastName')}
                  <input
                    type="text"
                    value={form.LastName}
                    tabIndex={4}
                    onChange={(e) => {
                      if (!editing) { // Only allow changes when not editing
                        setForm(prev => ({ ...prev, LastName: e.target.value }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        console.log('LastName: TAB/ENTER pressed, current tabIndex=4');
                        const nextField = findNextAvailableField(4);
                        if (nextField) {
                          nextField.focus();
                        }
                      }
                    }}
                    disabled={(form.LastName !== '' && !editing) || isLoadingEBarimt || editing}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      (form.LastName !== '' && !editing) || isLoadingEBarimt || editing ? 'bg-gray-200 border-gray-400 cursor-not-allowed text-gray-600' : 'border-gray-300'
                    }`}
                    readOnly={editing}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Эрх <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="IsEmployee"
                        tabIndex={5}
                        checked={true}
                        disabled={true}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                      />
                      <label htmlFor="IsEmployee" className="ml-2 block text-sm text-gray-900">
                        Ажилтан
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="IsCustomer"
                        tabIndex={6}
                        checked={form.Customers}
                        onChange={(e) => {
                          setForm(prev => ({ ...prev, Customers: e.target.checked }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Tab') {
                            e.preventDefault();
                            console.log('Customers: TAB/ENTER pressed, current tabIndex=6');
                            const nextField = findNextAvailableField(6);
                            if (nextField) {
                              nextField.focus();
                            }
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="IsCustomer" className="ml-2 block text-sm text-gray-900">
                        Харилцагч
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Банкны дансны дугаар
                    </label>
                    <input
                      type="text"
                      value={form.BankAccountnum}
                      placeholder="Банкны дансны дугаар"
                      tabIndex={7}
                      onChange={(e) => setForm(prev => ({ ...prev, BankAccountnum: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          console.log('BankAccountnum: TAB/ENTER pressed, current tabIndex=7');
                          const nextField = findNextAvailableField(7);
                          if (nextField) {
                            nextField.focus();
                          }
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
                    />
                  </div>
                  <div className="flex items-center mt-6">
                    <input
                      type="checkbox"
                      id="IsCitizen"
                      tabIndex={8}
                      checked={form.IsCitizen}
                      onChange={(e) => {
                        setForm(prev => ({ ...prev, IsCitizen: e.target.checked }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          console.log('IsCitizen: TAB/ENTER pressed, current tabIndex=8');
                          const nextField = findNextAvailableField(8);
                          if (nextField) {
                            nextField.focus();
                          }
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="IsCitizen" className="ml-2 block text-sm text-gray-900">
                    Хувь xүн эсэх
                    </label>
                  </div>
                </div>

                {(adding || editing) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Нууц үг {adding && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="password"
                      value={form.Password}
                      tabIndex={9}
                      placeholder={editing ? "Шинэ нууц үг" : ""}
                      onChange={(e) => setForm(prev => ({ ...prev, Password: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          console.log('Password: TAB/ENTER pressed, current tabIndex=9');
                          const nextField = findNextAvailableField(9);
                          if (nextField) {
                            nextField.focus();
                          }
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
          
                <button
                  onClick={() => {
                    setAdding(false);
                    setEditing(false);
                    setForm({
                      UserName: '',
                      FirstName: '',
                      LastName: '',
                      Password: '',
                      RoleOid: '',
                      BankAccountnum: '',
                      CustomerID: '',
                      Customers: false,
                      Employee: false,
                      IsCitizen: false
                    });
                  }}
                  tabIndex={adding ? 10 : 9}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Цуцлах
                </button>
                <button
                  onClick={async () => {
                    // Validate customer selection when Customers checkbox is checked
                    // Only validate for new users (adding mode), not when editing
                    if (adding && form.Customers && (!form.CustomerID || form.CustomerID.trim() === '')) {
                      setCustomerSelectionError('Харилцагч сонгоно уу');
                      return;
                    } else {
                      setCustomerSelectionError('');
                    }

                    if (adding) {
                      // Handle add
                      try {
                        const result = await api.post('/api/users', form);
                        if (result.success) {
                          // Show appropriate message based on backend response
                          const message = result.message || 'Хэрэглэгч амжилттай нэмэгдлээ';
                          setSuccessMessage(message);
                          setShowSuccessPopup(true);
                          setAdding(false);
                          fetchAppUsers();
                          setForm({
                            UserName: '',
                            FirstName: '',
                            LastName: '',
                            Password: '',
                            RoleOid: '',
                            BankAccountnum: '',
                            CustomerID: '',
                            Customers: false,
                            Employee: true, // Always true
                            IsCitizen: false
                          });
                          setCustomerSelectionError('');
                        } else {
                          alert('Алдаа: ' + result.message);
                        }
                      } catch (error) {
                        console.error('Error adding user:', error);
                        alert('Хэрэглэгч нэмэхэд алдаа гарлаа');
                      }
                    } else if (editing && editingUser) {
                      // Handle edit
                      try {
                        console.log('🔥 Frontend - Sending edit data:', {
                          UserName: form.UserName,
                          FirstName: form.FirstName,
                          LastName: form.LastName,
                          Customers: form.Customers,
                          Employee: form.Employee,
                          IsCitizen: form.IsCitizen,
                          BankAccountnum: form.BankAccountnum,
                          CustomerID: form.CustomerID
                        });
                        const result = await api.put(`/api/accountant/users/${editingUser.Oid}`, form);
                        console.log('🔥 Frontend - Edit response:', result);
                        if (result.success) {
                          setSuccessMessage('Хэрэглэгч амжилттай засагдлаа');
                          setShowSuccessPopup(true);
                          setEditing(false);
                          setEditingUser(null);
                          fetchAppUsers();
                          setForm({
                            UserName: '',
                            FirstName: '',
                            LastName: '',
                            Password: '',
                            RoleOid: '',
                            BankAccountnum: '',
                            CustomerID: '',
                            Customers: false,
                            Employee: true, // Always true
                            IsCitizen: false
                          });
                          setCustomerSelectionError('');
                        } else {
                          alert('Алдаа: ' + result.message);
                        }
                      } catch (error) {
                        console.error('Error editing user:', error);
                        alert('Хэрэглэгч засахад алдаа гарлаа');
                      }
                    }
                  }}
                  tabIndex={adding ? 9 : 8}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  disabled={!!registerNoError}
                >
                  {adding ? 'Нэмэх' : 'Хадгалах'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Success Popup */}
        <SuccessPopup
          message={successMessage}
          isVisible={showSuccessPopup}
          onClose={() => setShowSuccessPopup(false)}
          duration={3000}
        />
        
        {/* Error Popup */}
        <ErrorPopup
          message={errorMessage}
          isVisible={showErrorPopup}
          onClose={() => setShowErrorPopup(false)}
          duration={5000}
        />
        
        {/* Warning Popup */}
        <WarningPopup
          message={warningMessage}
          isVisible={showWarningPopup}
          onClose={() => setShowWarningPopup(false)}
          duration={4000}
        />
      </div>
    </LayoutWrapper>
  );
};

export default AccountantUserPage; 